import { useState, useEffect } from 'react';
import { X, Truck, Shield, CheckCircle, Clock, Users, PhoneCall, Archive } from 'lucide-react';

export const TrustBannerPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const hasOptedOut = localStorage.getItem('trustBannerDismissed');
    if (hasOptedOut) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    if (dontShowAgain) {
      localStorage.setItem('trustBannerDismissed', 'true');
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Small Side Banner - Bottom Right */}
      <div 
        className="fixed bottom-6 right-6 z-[200] animate-slideUp max-w-[300px] w-full"
        data-testid="trust-side-banner"
      >
        <div className="bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden">
          {/* Gold accent */}
          <div className="h-0.5 bg-gradient-to-r from-[#c5a059] to-[#d4af61]" />
          
          {/* Close */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 transition-colors p-0.5"
            aria-label="Close"
            data-testid="trust-banner-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="p-4">
            {/* Header */}
            <p className="text-[#c5a059] text-[10px] font-bold tracking-[0.15em] uppercase mb-3">
              Our Promise
            </p>

            {/* Trust Pillars - Compact */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="flex items-center gap-2 bg-[#f8f7f4] rounded px-2.5 py-2">
                <Truck className="w-3.5 h-3.5 text-[#c5a059] flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-[#1a1a1a] leading-tight">7-14 Days</p>
                  <p className="text-[8px] text-gray-400">Pan-India</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-[#f8f7f4] rounded px-2.5 py-2">
                <Shield className="w-3.5 h-3.5 text-[#c5a059] flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-[#1a1a1a] leading-tight">5-Year</p>
                  <p className="text-[8px] text-gray-400">Warranty</p>
                </div>
              </div>
            </div>

            {/* Quote */}
            <p className="text-gray-600 text-[10px] leading-relaxed italic mb-3">
              "Delivered <span className="text-[#c5a059] font-semibold">5000+ offices</span> on time."
            </p>

            {/* WHAT WE OFFER section */}
            <div className="border-t border-gray-100 pt-3 mb-3">
              <p className="text-[10px] font-bold tracking-[0.1em] text-gray-500 uppercase mb-2">
                What We Offer
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Users className="w-3.5 h-3.5 text-[#c5a059] flex-shrink-0" />
                  <p className="text-[10px] text-[#1a1a1a]"><span className="font-semibold">Modular Workstations</span> <span className="text-gray-400">for collaboration</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneCall className="w-3.5 h-3.5 text-[#c5a059] flex-shrink-0" />
                  <p className="text-[10px] text-[#1a1a1a]"><span className="font-semibold">Privacy Booths</span> <span className="text-gray-400">for focus</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Archive className="w-3.5 h-3.5 text-[#c5a059] flex-shrink-0" />
                  <p className="text-[10px] text-[#1a1a1a]"><span className="font-semibold">Storage Systems</span> <span className="text-gray-400">for organization</span></p>
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center gap-1 text-gray-500">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-[9px]">Factory Direct</span>
              </div>
              <div className="flex items-center gap-1 text-gray-500">
                <Clock className="w-3 h-3 text-blue-500" />
                <span className="text-[9px]">Since 2014</span>
              </div>
            </div>

            {/* Don't show again */}
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-3 h-3 rounded border-gray-300 text-[#c5a059] focus:ring-[#c5a059]"
              />
              <span className="text-gray-400 text-[9px]">Don't show again</span>
            </label>
          </div>
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(20px);
          }
          to { 
            opacity: 1; 
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};
